import * as React from 'react';
import styles from "../Service.module.scss";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowDownShortWide,
    faArrowDownWideShort,
    faFilter,
    faArrowDownUpAcrossLine
} from "@fortawesome/free-solid-svg-icons";
import {Popup, RadioGroup, TextBox} from "devextreme-react";
import {observer} from "mobx-react";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {ValueChangedInfo} from "devextreme/ui/editor/editor";
import {AppStoreContext} from "../../../Stores/AppStore";
import {ToolbarItem} from "devextreme-react/popup";
import {KeyString, SortingItem} from "../../../Models/ApiModels";
import {FilterItem} from "../../Filters/FilterSection";

enum FiltersView {
    hidden = 'hidden',
    sort = 'sort',
    filters = 'filters'
}

export type FilterSortingState = {
    desc?: boolean,
    search?: string,
    sorting?: string,
    filters?: {
        [key: string]: string | boolean
    },
}

type Props = {
    sortingItems: SortingItem[],
    filterItems: SortingItem[],
    customFilters?: JSX.Element,

};
export const ListFilterSorting = observer(({sortingItems, filterItems, customFilters}: Props) => {
    const context = useContext(AppStoreContext);
    const [view, setView] = useState<FiltersView>(FiltersView.hidden);
    const {setSortingState, sortingState} = context;

    useEffect(() => {
        return () => {
            setSortingState(null);
        };
    }, [setSortingState]);

    const _onSortClick = useCallback(() => {
        setView(FiltersView.sort);
    }, []);

    const _onFilterClick = useCallback(() => {
        setView(FiltersView.filters);
    }, []);

    const _changeSorting = useCallback((val: ValueChangedInfo) => {
        if (val.value) {
            const state = sortingState || {};
            setSortingState({...state, sorting: val.value, desc: state.desc === undefined ? true : state.desc});
        }
    }, [sortingState, setSortingState]);

    const _onSortDirectionClick = useCallback(() => {
        const state = sortingState || {};
        if (state.desc === undefined) {
            setView(FiltersView.sort);
        } else {
            setSortingState({...state, desc: !state.desc});
        }
    }, [sortingState, setSortingState]);

    const _clearFilters = useCallback((state: FilterSortingState) => {
        if (!Object.keys((state?.filters || {})).length) {
            delete state.filters;
        }
        setSortingState(Object.keys(state).length ? state : null);
    }, [setSortingState]);

    const _onReset = useCallback(() => {
        const state = {...(sortingState || {})};
        if (view === FiltersView.filters) {
            delete state.filters;
        } else {
            delete state.sorting;
            delete state.desc;
        }
        _clearFilters(state);
    }, [view, _clearFilters, sortingState]);

    const _onSearchChange = useCallback((value: string) => {
        const state = sortingState || {};
        setSortingState({...state, search: value || undefined});
    }, [sortingState, setSortingState]);


    const _onFilterChange = useCallback((key: string) => (select: string | boolean | null) => {
        const state = {...(sortingState || {})};
        const filters = state.filters || {};
        if (select === null) {
            delete filters[key];
        } else {
            filters[key] = select;
        }
        state.filters = filters;
        _clearFilters(state);
    }, [sortingState, _clearFilters]);

    const renderPopup = useCallback(() => {
        if (view === FiltersView.sort) {
            return (
                <RadioGroup
                    items={sortingItems}
                    value={sortingState?.sorting}
                    className={styles.radioGroup}
                    valueExpr="key"
                    displayExpr="label"
                    onValueChanged={_changeSorting}
                />
            );
        } else if (view === FiltersView.filters) {
            return (
                <div className={styles.filters}>
                    {customFilters}
                    {filterItems.map((filterItem) => {
                        return (
                            <FilterItem
                                key={filterItem.key}
                                isLoading={false}
                                label={filterItem.label}
                                options={filterItem.options || []}
                                onChange={_onFilterChange(filterItem.key)}
                                noClear={false}
                                selected={sortingState?.filters?.[filterItem.key]}
                            />
                        );
                    })}
                </div>
            );
        }
        return null;


    }, [view, sortingItems, sortingState, _onFilterChange, filterItems, _changeSorting, customFilters]);

    const resetButtonOptions = useMemo(() => ({
        text: 'CLEAR',
        rtlEnabled: true,
        icon: 'clearformat',
        type: 'danger',
        stylingMode: 'text',
        onClick: _onReset,
    }), [_onReset]);

    return (
        <>
            <div className={styles.sorting}>
                <div className={styles.search}>
                    <TextBox
                        value={sortingState?.search}
                        onValueChange={_onSearchChange}
                        placeholder="Search..."
                        showClearButton={true}
                    />
                </div>
                <div className={styles.group}>
                    <div className={`${styles.name} ${sortingState?.desc !== undefined ? styles.active : ''}`}
                         onClick={_onSortClick}>Sort By
                    </div>
                    <div className={`${styles.icon} ${sortingState?.desc !== undefined ? styles.active : ''}`}
                         onClick={_onSortDirectionClick}>
                        <FontAwesomeIcon
                            icon={sortingState?.desc === undefined ? faArrowDownUpAcrossLine : sortingState?.desc ? faArrowDownShortWide : faArrowDownWideShort}/>
                    </div>
                </div>
                <div className={styles.group} onClick={_onFilterClick}>
                    <div className={`${styles.icon} ${sortingState?.filters !== undefined ? styles.active : ''}`}>
                        <FontAwesomeIcon icon={faFilter}/>
                    </div>
                </div>
            </div>
            <Popup
                title={view === FiltersView.sort ? "Sort By" : "Filters"}
                dragEnabled={false}
                hideOnOutsideClick={true}
                height="auto"
                maxWidth={360}
                visible={view !== FiltersView.hidden}
                onHiding={() => setView(FiltersView.hidden)}
                showCloseButton={false}
                contentRender={renderPopup}
            >
                <ToolbarItem
                    widget="dxButton"
                    visible={(view === FiltersView.sort && !!sortingState?.sorting) || (view === FiltersView.filters && !!sortingState?.filters)}
                    toolbar="top"
                    location="after"
                    cssClass={styles.reset}
                    options={resetButtonOptions}
                />

            </Popup>
        </>
    );
});

export const GetOptionsFromList = (vales?: string[], labels?: KeyString) => {
    const list = Array.from(new Set(vales || []));
    return list.map(el => {
        const label = labels && labels[el] ? labels[el] : String(el);
        const value = String(el);
        return {label, value};
    });
};
